import { useEffect, useMemo } from "react";
import usePagination from "@/hooks/pagination";
import { useFilterState } from "@/hooks/useFilterState";
import { useListaRemota } from "@/hooks/useListaRemota";
import { useOpcoesCadastro } from "@/hooks/useOpcoesCadastro";
import { useAuth } from "@/context/AuthContext";
import { listLojas, type Loja, type RelatorioFiltro } from "@/api/escala.service";
import { competenciaAtual } from "@/components/escala/competenciaAtual";

// Filtro (o digitado e o aplicado), lojas do seletor e paginação de um relatório.
export function useFiltroRelatorio<F extends RelatorioFiltro>(inicial: F) {
  const { user } = useAuth();
  const clientId = user!.loggedClient!.id;
  const paginacao = usePagination();
  const filtro = useFilterState<F>(inicial, () => paginacao.setPage(0));
  const { opcoes } = useOpcoesCadastro<{ lojas: Loja[] }>(
    { lojas: { buscar: () => listLojas(clientId), mensagemErro: "Erro ao carregar lojas" } },
    [clientId],
  );
  // Ano e mês sempre têm valor; conta só o que foi escolhido além deles.
  const filtrosAtivos = Object.entries(filtro.filtros).filter(([chave, valor]) => chave !== "ano" && chave !== "mes" && valor).length;

  return { clientId, lojas: opcoes.lojas, filtrosAtivos, paginacao, ...filtro };
}

export type EstadoFiltroRelatorio<F extends RelatorioFiltro = RelatorioFiltro> = ReturnType<typeof useFiltroRelatorio<F>>;

interface OpcoesRelatorio<T, F> {
  buscar: (filtros: F, clientId: string) => Promise<T[]>;
  // Fora do componente (ou estável): entra nas dependências da ordenação.
  ordenar: (a: T, b: T, lojas: Loja[]) => number;
  mensagemErro: string;
  inicial?: F;
}

// Relatório que vem inteiro do servidor e pagina no cliente: busca a cada filtro aplicado, ordena e corta a página.
export function useRelatorio<T, F extends RelatorioFiltro = RelatorioFiltro>({ buscar, ordenar, mensagemErro, inicial }: OpcoesRelatorio<T, F>) {
  const filtro = useFiltroRelatorio<F>(inicial ?? (competenciaAtual() as F));
  const { clientId, filtros, lojas } = filtro;
  const { total, setTotal, page, setPage, rowsPerPage, setRowsPerPage } = filtro.paginacao;

  const { itens, carregouAlgumaVez } = useListaRemota(() => buscar(filtros, clientId), [clientId, filtros], mensagemErro);

  const ordenadas = useMemo(() => [...itens].sort((a, b) => ordenar(a, b, lojas)), [itens, ordenar, lojas]);
  useEffect(() => setTotal(ordenadas.length), [ordenadas, setTotal]);

  // Props de paginação do DataTable, prontas pra espalhar.
  const tabela = {
    data: ordenadas.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    total,
    page,
    rowsPerPage,
    onPageChange: setPage,
    onRowsPerPageChange: setRowsPerPage,
  };

  return { ...filtro, linhas: itens, ordenadas, tabela, carregando: !carregouAlgumaVez };
}

export const porNome = <T extends { nome: string }>(a: T, b: T) => a.nome.localeCompare(b.nome);
